/**
 * ImageVariationsGrid Component
 *
 * Displays a 2x2 grid of generated image variations for selection.
 * Used in the AI Image Generation modal for choosing the best variation.
 */

import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface GeneratedImage {
  url: string;
  width: number;
  height: number;
}

interface ImageVariationsGridProps {
  images: GeneratedImage[];
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  aspectRatio: string;
}

export function ImageVariationsGrid({
  images,
  selectedIndex,
  onSelect,
  aspectRatio,
}: ImageVariationsGridProps) {
  // Calculate aspect ratio for container
  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case '1:1':
        return 'aspect-square';
      case '16:9':
        return 'aspect-video';
      case '4:3':
        return 'aspect-[4/3]';
      default:
        return 'aspect-video';
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      {images.map((image, index) => (
        <button
          key={index}
          onClick={() => onSelect(index)}
          className={cn(
            'relative group rounded-lg overflow-hidden border-2 transition-all duration-200',
            'hover:border-primary/50 hover:shadow-lg',
            'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
            selectedIndex === index
              ? 'border-primary shadow-md ring-2 ring-primary/20'
              : 'border-border/50'
          )}
        >
          {/* Image container with aspect ratio */}
          <div className={cn('relative w-full', getAspectRatioClass())}>
            <img
              src={image.url}
              alt={`Generated variation ${index + 1}`}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />

            {/* Hover overlay */}
            <div
              className={cn(
                'absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors',
                selectedIndex === index && 'bg-primary/10'
              )}
            />

            {/* Selection indicator */}
            {selectedIndex === index && (
              <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center shadow-lg">
                <Check className="h-4 w-4 text-primary-foreground" />
              </div>
            )}

            {/* Variation number badge */}
            <div
              className={cn(
                'absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium',
                'bg-black/50 text-white backdrop-blur-sm',
                selectedIndex === index && 'bg-primary text-primary-foreground'
              )}
            >
              #{index + 1}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

export default ImageVariationsGrid;
